'use client';

import { useEffect, useState, useRef, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useUser } from '@clerk/nextjs';
import Header from '@/components/common/Header';
import Icon from '@/components/ui/AppIcon';
import { PRICING } from '@/lib/payments/constants';
import DodoCheckoutButton from '@/components/molecules/DodoCheckoutButton';

function PricingContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, isLoaded } = useUser();
  const [credits, setCredits] = useState<number | null>(null);
  const [previousCredits, setPreviousCredits] = useState<number | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCanceled, setShowCanceled] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [creditsPending, setCreditsPending] = useState(false);
  const paymentProcessedRef = useRef<string | null>(null);

  const verifyAndAddCredits = async (paymentId: string) => {
    try {
      console.log('Calling verify-payment endpoint for:', paymentId);
      const response = await fetch('/api/payments/verify-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ paymentId }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('Payment verified, credits added:', data.creditsAdded);
        setCreditsPending(false);
        setShowSuccess(true);
        setCheckoutError(null);
        // Refresh credits to show new balance
        await fetchCredits();
      } else {
        console.error('Payment verification failed:', data.error);
        setCreditsPending(false);
        
        // Check if payment failed
        if (data.status === 'failed' || data.error?.includes('not yet completed')) {
          setCheckoutError('Payment failed or was not completed. Please try again or contact support if the issue persists.');
        } else {
          // Still poll for credits in case webhook processes it
          const pollInterval = setInterval(() => {
            fetchCredits();
          }, 2000);
          
          setTimeout(() => {
            clearInterval(pollInterval);
            setCreditsPending(false);
          }, 30000);
        }
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      // Fallback to polling
      const pollInterval = setInterval(() => {
        fetchCredits();
      }, 2000);
      
      setTimeout(() => {
        clearInterval(pollInterval);
        setCreditsPending(false);
      }, 30000);
    }
  };

  useEffect(() => {
    const success = searchParams.get('success') === 'true';
    const paymentId = searchParams.get('payment_id');
    const paymentStatus = searchParams.get('status');
    
    // Check if payment failed
    if (paymentStatus === 'failed') {
      setCreditsPending(false);
      setShowSuccess(false);
      setCheckoutError('Payment failed. Please try again or contact support if the issue persists.');
      return;
    }
    
    if (success && paymentId && paymentProcessedRef.current !== paymentId) {
      // Mark this payment as being processed
      paymentProcessedRef.current = paymentId;
      
      // Store current credits before processing
      if (credits !== null) {
        setPreviousCredits(credits);
      }
      setCreditsPending(true);
      
      // Verify the payment and add credits
      console.log('Verifying payment:', paymentId);
      verifyAndAddCredits(paymentId);
    } else if (success && !paymentId) {
      // Fallback: just refresh credits and poll (no payment_id in URL)
      if (credits !== null) {
        setPreviousCredits(credits);
      }
      setCreditsPending(true);
      fetchCredits();
      
      // Poll for credits to be added (webhook might be delayed)
      const pollInterval = setInterval(() => {
        fetchCredits();
      }, 2000); // Poll every 2 seconds
      
      // Stop polling after 30 seconds
      const timeout = setTimeout(() => {
        clearInterval(pollInterval);
        setCreditsPending(false);
      }, 30000);
      
      return () => {
        clearInterval(pollInterval);
        clearTimeout(timeout);
      };
    }
    
    if (searchParams.get('canceled') === 'true') {
      setShowCanceled(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (isLoaded && user) {
      fetchCredits();
    }
  }, [isLoaded, user]);

  const fetchCredits = async () => {
    try {
      const response = await fetch('/api/user/credits');
      const data = await response.json();
      if (response.ok) {
        const newCredits = data.credits;
        
        // Check if credits increased (payment processed)
        if (previousCredits !== null && newCredits > previousCredits) {
          setCreditsPending(false);
          setShowSuccess(true);
          console.log(`Credits increased from ${previousCredits} to ${newCredits}`);
        }
        
        // Store previous credits for comparison
        if (previousCredits === null) {
          setPreviousCredits(newCredits);
        } else if (newCredits !== previousCredits) {
          setPreviousCredits(newCredits);
        }
        
        setCredits(newCredits);
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  };

  const handleCheckoutSuccess = () => {
    setShowSuccess(true);
    setCheckoutError(null);
    // Refresh credits after successful purchase
    fetchCredits();
    // Clear success message from URL if present
    if (searchParams.get('success') === 'true') {
      router.replace('/pricing');
    }
  };

  const handleCheckoutError = (error: string) => {
    setCheckoutError(error);
    setShowSuccess(false);
  };

  // Do NOT gate rendering on `!isLoaded` — the pricing content, FAQ, and
  // page copy must be present in the initial server-rendered HTML so
  // crawlers (including non-JS AI crawlers) can see it. The `user`-dependent
  // bits below (credits balance, checkout vs. sign-in button) already
  // degrade gracefully to their signed-out state while auth is loading.
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8">
        {/* Success Message */}
        {showSuccess && (
          <div className="mb-8 p-4 bg-success/10 border border-success/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Icon name="CheckCircleIcon" size={24} className="text-success" variant="solid" />
              <div>
                <h4 className="font-semibold text-success">Payment Successful!</h4>
                <p className="text-sm text-text-secondary">
                  {PRICING.CREDITS_PER_PACKAGE} credits have been added to your account. You can now analyze offer letters!
                </p>
              </div>
              <button
                onClick={() => setShowSuccess(false)}
                className="ml-auto p-1 hover:bg-success/20 rounded"
              >
                <Icon name="XMarkIcon" size={20} className="text-success" />
              </button>
            </div>
          </div>
        )}

        {/* Credits Pending Message */}
        {creditsPending && !showSuccess && searchParams.get('success') === 'true' && (
          <div className="mb-8 p-4 bg-primary/10 border border-primary/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              <div>
                <h4 className="font-semibold text-primary">Processing Payment...</h4>
                <p className="text-sm text-text-secondary">
                  Your payment was successful! We're processing your credits. This usually takes a few seconds.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Canceled Message */}
        {showCanceled && (
          <div className="mb-8 p-4 bg-warning/10 border border-warning/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Icon name="ExclamationTriangleIcon" size={24} className="text-warning" variant="solid" />
              <div>
                <h4 className="font-semibold text-warning">Payment Canceled</h4>
                <p className="text-sm text-text-secondary">
                  Your payment was canceled. No charges were made.
                </p>
              </div>
              <button
                onClick={() => setShowCanceled(false)}
                className="ml-auto p-1 hover:bg-warning/20 rounded"
              >
                <Icon name="XMarkIcon" size={20} className="text-warning" />
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {checkoutError && (
          <div className="mb-8 p-4 bg-error/10 border border-error/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Icon name="XCircleIcon" size={24} className="text-error" variant="solid" />
              <div>
                <h4 className="font-semibold text-error">Checkout Error</h4>
                <p className="text-sm text-text-secondary">{checkoutError}</p>
              </div>
              <button
                onClick={() => setCheckoutError(null)}
                className="ml-auto p-1 hover:bg-error/20 rounded"
              >
                <Icon name="XMarkIcon" size={20} className="text-error" />
              </button>
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className="mb-12 flex items-start justify-between gap-8">
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-text-primary mb-4">
              Get Analysis Credits
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl">
              Purchase credits to analyze your job offer letters with our AI-powered analysis engine.
              Each analysis provides comprehensive insights, risk assessment, and negotiation guidance.
            </p>
          </div>

          {/* Current Credits Display */}
          {user && credits !== null && (
            <div className="flex-shrink-0">
              <div className="inline-flex items-center gap-4 px-8 py-6 bg-surface border-2 border-primary rounded-xl shadow-md">
                <Icon name="CurrencyDollarIcon" size={36} className="text-primary" />
                <div className="flex flex-col">
                  <span className="text-base text-text-secondary font-medium">Your Balance</span>
                  <span className="text-4xl font-bold text-primary">{credits} credits</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Pricing Card and FAQ Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Pricing Card */}
          <div className="max-w-md">
            <div className="bg-surface border-2 border-primary rounded-2xl overflow-hidden shadow-lg">
              {/* Card Header */}
              <div className="bg-surface border-b border-border px-6 py-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4">
                  <Icon name="SparklesIcon" size={16} />
                  Best Value
                </div>
                <h2 className="text-2xl font-bold text-text-primary mb-2">Analysis Credits</h2>
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl font-bold text-primary">{PRICING.PRICE_DISPLAY}</span>
                </div>
                <p className="text-text-secondary mt-2">for {PRICING.CREDITS_PER_PACKAGE} analysis credits</p>
              </div>

            {/* Card Body */}
            <div className="px-6 py-8">
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                  <span className="text-text-primary">{PRICING.CREDITS_PER_PACKAGE} offer letter analyses</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                  <span className="text-text-primary">Comprehensive risk assessment</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                  <span className="text-text-primary">Personalized negotiation guidance</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                  <span className="text-text-primary">Export reports in PDF/JSON</span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                  <span className="text-text-primary">Credits never expire</span>
                </li>
              </ul>

              {user ? (
                <DodoCheckoutButton
                  onSuccess={handleCheckoutSuccess}
                  onError={handleCheckoutError}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-lg transition-all duration-200 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:bg-primary/60 disabled:cursor-not-allowed"
                />
              ) : (
                <button
                  onClick={() => router.push('/login')}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-lg transition-all duration-200 bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  <Icon name="CreditCardIcon" size={20} />
                  <span>Sign In to Purchase</span>
                </button>
              )}

              <p className="text-center text-sm text-text-secondary mt-4">
                Secure payment powered by Dodo Payments
              </p>
            </div>
          </div>
          </div>

          {/* FAQ Section */}
          <div>
            <h3 className="text-2xl font-bold text-text-primary mb-8">
              Frequently Asked Questions
            </h3>
            <div className="space-y-4">
            <div className="bg-surface border border-border rounded-lg p-5">
              <h4 className="font-semibold text-text-primary mb-2">How do credits work?</h4>
              <p className="text-text-secondary text-sm">
                Each credit allows you to analyze one offer letter. When you upload a document and run an analysis, one credit is deducted from your balance.
              </p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-5">
              <h4 className="font-semibold text-text-primary mb-2">Do credits expire?</h4>
              <p className="text-text-secondary text-sm">
                No, your credits never expire. Use them whenever you need to analyze an offer letter.
              </p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-5">
              <h4 className="font-semibold text-text-primary mb-2">What if the analysis fails?</h4>
              <p className="text-text-secondary text-sm">
                Credits are only deducted when an analysis is successfully completed. If something goes wrong, your credit won't be charged.
              </p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-5">
              <h4 className="font-semibold text-text-primary mb-2">Can I get a refund?</h4>
              <p className="text-text-secondary text-sm">
                If you're not satisfied with our service, please contact us and we'll work with you to resolve any issues.
              </p>
            </div>
          </div>
        </div>
        </div>

        {/* Back to Upload */}
        <div className="text-center mt-12">
          <button
            onClick={() => router.push('/document-upload')}
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
          >
            <Icon name="ArrowLeftIcon" size={20} />
            <span>Back to Document Upload</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PricingPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-muted rounded w-1/3 mx-auto" />
            <div className="h-64 bg-muted rounded max-w-md mx-auto" />
          </div>
        </div>
      </div>
    }>
      <PricingContent />
    </Suspense>
  );
}

